// Global Instructions Rule Applied!
//
// Shared "Merge fields" control — a click-to-copy chip strip of the
// placeholder tokens the dispatch edge function substitutes (subject + body).
// Extracted from the compose form so the campaign compose tab and the
// per-recipient override drawer expose the *same* authoring affordance and
// read the single MERGE_FIELDS source of truth (kept in lockstep with the
// edge fn's substituteMergeFields keys via constants/mergeFields.ts).
//
// Self-contained: renders its own trigger Button + Modal and owns the
// open / hover state, so any surface drops in <MergeFieldsButton /> with no
// wiring. Copy-to-clipboard (not cursor-insert) keeps behaviour identical to
// the original compose control — the user pastes into subject or body.
import React, { useState } from 'react'
import { Button, Modal, Space, Tag, Tooltip } from 'antd'
import { TagsOutlined, CopyOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { MERGE_FIELDS, type MergeField } from '../constants/mergeFields.ts'

interface MergeFieldsButtonProps {
  /** AntD button size — defaults to 'small' to match the toolbars it sits in. */
  size?: 'small' | 'middle' | 'large'
  /** Button label override (default "Merge fields"). */
  label?: string
  /** Render the trigger as a borderless link button (used in tight toolbars). */
  type?: 'default' | 'link'
  /** Field list override (default: the campaign MERGE_FIELDS). Stage-action
   *  surfaces pass DEAL_MERGE_FIELDS. */
  fields?: MergeField[]
}

export default function MergeFieldsButton({
  size = 'small',
  label = 'Merge fields',
  type = 'default',
  fields = MERGE_FIELDS
}: MergeFieldsButtonProps) {
  const [open, setOpen] = useState(false)
  const [hovered, setHovered] = useState<string | null>(null)

  return (
    <>
      <Tooltip title='Insert a merge field placeholder'>
        <Button
          size={size}
          type={type}
          icon={<TagsOutlined />}
          onClick={() => setOpen(true)}
          style={type === 'link' ? { padding: 0 } : undefined}
        >
          {label}
        </Button>
      </Tooltip>
      <Modal
        title='Merge fields'
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={520}
        destroyOnClose
      >
        <p style={{ marginTop: 0, color: '#8c8c8c' }}>
          Click a placeholder to copy. Paste it into the subject or body where you want
          the dynamic value to appear.
        </p>
        <Space wrap size={[6, 8]}>
          {fields.map((f) => {
            const isHovered = hovered === f.placeholder
            return (
              <Tooltip title={f.tooltip} key={f.placeholder}>
                <Tag
                  onMouseEnter={() => setHovered(f.placeholder)}
                  onMouseLeave={() =>
                    setHovered((curr) => (curr === f.placeholder ? null : curr))
                  }
                  style={{
                    cursor: 'pointer',
                    fontFamily: 'monospace',
                    userSelect: 'none',
                    borderColor: isHovered ? '#1677ff' : undefined,
                    background: isHovered ? '#e6f4ff' : undefined,
                    margin: 0
                  }}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(f.placeholder)
                      toast.success(`Copied ${f.placeholder}`)
                      // One copy per session — close the modal so the user can
                      // paste straight away.
                      setOpen(false)
                    } catch {
                      showErrorToast('Clipboard copy failed — select and copy manually.')
                    }
                  }}
                >
                  {f.placeholder}
                  <CopyOutlined
                    style={{
                      marginLeft: 6,
                      fontSize: 11,
                      opacity: isHovered ? 1 : 0,
                      transition: 'opacity 120ms ease',
                      color: '#1677ff'
                    }}
                  />
                </Tag>
              </Tooltip>
            )
          })}
        </Space>
      </Modal>
    </>
  )
}
